/*
 * O script da landing pública — vanilla, e sem importar nada.
 *
 * ZERO `import` de pacote é uma regra, não um acaso: a landing é a segunda
 * entrada do Vite (`vite.config.ts`, `rollupOptions.input.landing`), e qualquer
 * `import` daqui arrastaria o pacote para o chunk `landing-*.js` — o React
 * inclusive, se alguém importasse um componente por conveniência. A cerca
 * `a-landing-e-publica-e-nao-paga-fonte.test.ts` lê este arquivo e recusa a
 * linha. O que a página precisa cabe em DOM puro, e a página inteira se lê sem
 * ele: cada função abaixo só acrescenta conforto — a faixa da dobra 4
 * inclusive, que sem script é uma tira estática rolável na horizontal.
 */

const CELULAR = '(max-width: 767px)';
const MENOS_MOVIMENTO = '(prefers-reduced-motion: reduce)';

function limitar(valor: number, minimo: number, maximo: number): number {
  return Math.min(maximo, Math.max(minimo, valor));
}

/**
 * Revelação ao rolar, respeitando quem pediu menos movimento.
 *
 * Com `prefers-reduced-motion: reduce` o observador nem é ligado: o CSS já
 * mostra tudo. Sem `IntersectionObserver` o mesmo: tudo visível de saída — a
 * página nunca depende do script para ser lida.
 */
function ligarRevelacao(): void {
  const blocos = document.querySelectorAll<HTMLElement>('[data-revelar]');
  if (blocos.length === 0) return;

  const menosMovimento = window.matchMedia(MENOS_MOVIMENTO).matches;
  if (menosMovimento || typeof IntersectionObserver === 'undefined') {
    for (const bloco of blocos) bloco.classList.add('revelado');
    return;
  }

  const observador = new IntersectionObserver(
    (entradas) => {
      for (const entrada of entradas) {
        if (!entrada.isIntersecting) continue;
        entrada.target.classList.add('revelado');
        observador.unobserve(entrada.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
  );
  for (const bloco of blocos) observador.observe(bloco);
}

/**
 * O menu do celular: os quatro âncoras e «Entrar» (último item) recolhem atrás
 * do botão; «Criar conta grátis» fica sempre fora dele (ordem do copywriter,
 * dobra 0). Clicar num item fecha o menu, e `Escape` também.
 */
function ligarMenu(): void {
  const botao = document.querySelector<HTMLButtonElement>('[data-menu]');
  const ancoras = document.getElementById('ancoras');
  if (!botao || !ancoras) return;

  const fechar = () => {
    botao.setAttribute('aria-expanded', 'false');
    delete ancoras.dataset.aberto;
  };
  botao.addEventListener('click', () => {
    const aberto = botao.getAttribute('aria-expanded') === 'true';
    if (aberto) fechar();
    else {
      botao.setAttribute('aria-expanded', 'true');
      ancoras.dataset.aberto = '';
    }
  });
  ancoras.addEventListener('click', (evento) => {
    if ((evento.target as HTMLElement).closest('a')) fechar();
  });
  /* Rodada 21: tocar fora do painel (na página que ficou por baixo) também fecha — no
     telefone é o gesto que a mão faz primeiro. A barra fica de fora, para o toque no
     próprio botão continuar a ser o dele. */
  document.addEventListener('click', (evento) => {
    if (botao.getAttribute('aria-expanded') !== 'true') return;
    if ((evento.target as HTMLElement).closest('.barra')) return;
    fechar();
  });
  document.addEventListener('keydown', (evento) => {
    if (evento.key !== 'Escape' || botao.getAttribute('aria-expanded') !== 'true') return;
    fechar();
    botao.focus();
  });
  window.matchMedia(CELULAR).addEventListener('change', (mq) => {
    if (!mq.matches) fechar();
  });
}

/**
 * A moldura de espera: uma captura que ainda não existe não vira ícone de
 * imagem quebrada — a moldura ganha `captura--ausente` e o CSS escreve o nome
 * do arquivo esperado. É a espera honesta: nada de print falso.
 */
function ligarMolduraDeEspera(): void {
  const marcar = (img: HTMLImageElement) => {
    img.closest('.captura')?.classList.add('captura--ausente');
  };
  document.addEventListener(
    'error',
    (evento) => {
      const alvo = evento.target;
      if (alvo instanceof HTMLImageElement && alvo.closest('.captura')) marcar(alvo);
    },
    true,
  );
  for (const img of document.querySelectorAll<HTMLImageElement>('.captura img')) {
    if (img.complete && img.naturalWidth === 0) marcar(img);
  }
}

/**
 * As SETE perguntas da v5: abertas no desktop; no celular ficam abertas a
 * primeira, a SEGUNDA e a última. A segunda («Vocês sabem o que meu lead está
 * pensando?») é obrigatória no aberto porque é a que responde à manchete da
 * página — esconder a resposta da manchete atrás de um «+» deixaria a acusação
 * de adivinhação de pé. A última, «O que o Vestigo não faz?», continua aberta
 * pela razão de sempre. As quatro do meio recolhem. A tabela de fontes da dobra
 * 9 segue a mesma regra: aberta no desktop, recolhida atrás de «Ver as fontes»
 * no celular. E os seis esquadrões da dobra 6 recolhem, menos o primeiro: são
 * trinta agentes, e no celular eles são uma coluna só.
 */
function ajustarNoCelular(): void {
  if (!window.matchMedia(CELULAR).matches) return;
  const perguntas = document.querySelectorAll<HTMLDetailsElement>('.pergunta');
  const ultima = perguntas.length - 1;
  perguntas.forEach((pergunta, indice) => {
    if (indice > 1 && indice < ultima) pergunta.open = false;
  });
  const fontes = document.querySelector<HTMLDetailsElement>('[data-fontes]');
  if (fontes) fontes.open = false;
  document.querySelectorAll<HTMLDetailsElement>('[data-esquadrao]').forEach((esquadrao, indice) => {
    if (indice > 0) esquadrao.open = false;
  });
}

/**
 * ── A faixa da dobra 4: a rolagem vertical avança uma tira horizontal ──────
 *
 * Como funciona: a `.fluxo__pista` é alta (6 viewports) e o `.fluxo__palco`
 * fica grudado dentro dela. A cada quadro o script lê quanto da pista já
 * passou (progresso 0–1) e escreve custom properties; o CSS deriva o resto:
 *
 *   --x        deslocamento da faixa (px) — as cenas entram pela direita
 *   --d        por cena: distância ao foco, em cenas (+ à frente, − para trás);
 *              dela o CSS tira opacidade, escala, a gota que desce e a marca
 *   --zoom     o fecho: a câmera recua até o trilho inteiro caber na tela
 *   --recolhe  0→1 no fecho: na primeira metade rótulos, cartões e arcos
 *              somem; na segunda entram o rótulo do fecho e as setas de
 *              retorno redesenhadas. As marcas ficam o tempo todo, e a linha
 *              passa ao `.fecho__trilho` (em px de tela: `--fecho-x1`,
 *              `--fecho-w`, `--fecho-o`) no primeiro quarto do recolhimento.
 *   --pessoa-x o ponto (a pessoa) em px da faixa — percorre o trilho
 *   --no       0→1 no passo 5: o ponto vira o nó «contato» (rodada 8)
 *
 * Fase A (progresso 0 → 0,88): a faixa desliza da cena 1 à 11. Fase B (0,88 →
 * 1): a cena 12, o fecho. Só entra em desktop (≥ 1024) e sem
 * `prefers-reduced-motion`; fora disso a classe `fluxo--anima` nunca é posta
 * e a tira fica estática (ou empilhada, no celular).
 */
function ligarFluxo(): void {
  const secao = document.querySelector<HTMLElement>('.fluxo');
  const pista = document.querySelector<HTMLElement>('[data-fluxo]');
  const palco = secao?.querySelector<HTMLElement>('.fluxo__palco');
  const faixa = document.querySelector<HTMLElement>('[data-faixa]');
  if (!secao || !pista || !palco || !faixa) return;

  const cenas = [...faixa.querySelectorAll<HTMLElement>('.cena[data-cena]')];
  const fecho = secao.querySelector<HTMLElement>('.cena--fecho');
  const setasDoFecho = secao.querySelector<SVGSVGElement>('[data-fecho-setas]');
  const trilhoDoFecho = secao.querySelector<HTMLElement>('[data-fecho-trilho]');
  const progresso = secao.querySelector<HTMLElement>('.fluxo__progresso');
  const passos = [...secao.querySelectorAll<HTMLLIElement>('.progresso li')];
  const total = cenas.length;
  if (total === 0) return;

  /* A fase B (o fecho em linha, reposto na rodada 11) tem 12% da pista: a câmera recua,
     as contas somem, os dois arcos são desenhados e o nó «contato» para na ponta. A rede
     precisava de 22% porque construía dezessete gestos; a linha não. */
  const FASE_A = 0.88;
  const BARRA = 60;
  /* A faixa alinha pela margem do miolo (a mesma da barra de progresso). */
  let margem = 48;

  const menosMovimento = window.matchMedia(MENOS_MOVIMENTO);
  const largo = window.matchMedia('(min-width: 1024px)');
  const podeAnimar = () => !menosMovimento.matches && largo.matches;

  let ativo = false;
  let agendado = false;
  let ultimoProgresso = -1;
  let larguraPalco = 0;
  let larguraCena = 0;
  let larguraFaixa = 0;
  let percurso = 1;

  /* Onde a pessoa para no fim do fecho, em px da faixa. */
  const pontoFinal = () => larguraFaixa - larguraCena * 0.2;

  /*
   * Um ponto da faixa escrito na marcação: "cena:fração" (cena contada de 0,
   * fração da largura dela) ou "fim" (onde a pessoa para). Devolve px da faixa,
   * sem zoom. Nenhuma forma de jornada mora neste arquivo — a rodada 7 tirou
   * daqui o «cena 4 → cena 1» que estava escrito à mão.
   */
  const pontoDaFaixa = (ref: string | undefined, padrao: number): number => {
    if (ref === 'fim') return pontoFinal();
    const [cena = Number.NaN, fracao = Number.NaN] = (ref ?? '').split(':').map(Number);
    if (!Number.isFinite(cena) || !Number.isFinite(fracao)) return padrao;
    return (cena + fracao) * larguraCena;
  };

  /*
   * As ligações do fecho (a última cena), na escala do trilho recolhido. Os arcos
   * das cenas, escalados a ~0,2, viravam um borrão de quarenta pixels; estes são
   * desenhados em px de tela, com a ponta do tamanho de sempre, e só aparecem
   * depois de os cartões sumirem (o CSS liga a opacidade ao `--recolhe-fecho`).
   *
   * Cada `path[data-de]` é uma ligação genérica: `data-de`/`data-para` (ver
   * `pontoDaFaixa`), `data-lado` = "cima" (padrão) ou "baixo" do trilho,
   * `data-altura` em px. Serve ao sinal que volta ao anúncio, a uma volta de
   * recompra e a entradas convergindo — a forma chega pela copy.
   */
  const desenharSetasDoFecho = () => {
    if (!setasDoFecho || larguraFaixa === 0) return;
    const zoomFinal = (larguraPalco - 2 * margem) / larguraFaixa;
    const emTela = (ref: string | undefined) => margem + zoomFinal * pontoDaFaixa(ref, 0);
    const meio = 90;
    setasDoFecho.setAttribute('viewBox', `0 0 ${larguraPalco} 180`);
    for (const seta of setasDoFecho.querySelectorAll<SVGPathElement>('path[data-de]')) {
      const origem = emTela(seta.dataset.de);
      const alvo = emTela(seta.dataset.para);
      const altura = Number(seta.dataset.altura ?? 50);
      const baixo = seta.dataset.lado === 'baixo';
      const base = baixo ? meio + 7 : meio - 7;
      const curva = baixo ? base + altura : base - altura;
      seta.setAttribute(
        'd',
        `M ${origem.toFixed(1)} ${base} C ${origem.toFixed(1)} ${curva}, ${alvo.toFixed(1)} ${curva}, ${alvo.toFixed(1)} ${base}`,
      );
    }
  };

  const medir = () => {
    larguraPalco = palco.clientWidth;
    const lista = progresso?.querySelector<HTMLElement>('.progresso');
    if (lista) margem = Math.max(20, lista.getBoundingClientRect().left);
    larguraCena = cenas[0]?.offsetWidth ?? 0;
    larguraFaixa = faixa.offsetWidth;
    percurso = Math.max(1, pista.offsetHeight - palco.offsetHeight);
    ultimoProgresso = -1;
    desenharSetasDoFecho();
  };

  const suavizar = (t: number) => t * t * (3 - 2 * t);

  const pintar = () => {
    agendado = false;
    if (!ativo) return;
    const topo = pista.getBoundingClientRect().top - BARRA;
    const p = limitar(-topo / percurso, 0, 1);
    if (p === ultimoProgresso) return;
    ultimoProgresso = p;

    /* O foco fica a 60% da largura: a cena anterior inteira à esquerda (é para
       ela que os arcos da cena 5 voltam), a próxima espreitando à direita. */
    const foco = larguraPalco * 0.64;
    let cena: number;
    let x: number;
    let zoom = 1;
    let recolhe = 0;
    let pessoaX: number;

    if (p <= FASE_A) {
      cena = (p / FASE_A) * (total - 1);
      /* A faixa nasce presa à margem esquerda: a pessoa anda primeiro, e só
         quando ela alcança o foco a câmera passa a segui-la. */
      x = Math.min(margem, foco - (cena + 0.5) * larguraCena);
      pessoaX = (cena + 0.5) * larguraCena;
    } else {
      /* `t` corre o fecho inteiro (a rede, o título); a câmera recua só na primeira metade
         dele (`z`), enquanto as contas da linha somem. */
      const t = (p - FASE_A) / (1 - FASE_A);
      const z = suavizar(Math.min(1, t / 0.5));
      /* A cena 11 continua a passar (até 0,6 além do foco): a última marca é escrita. */
      cena = total - 1 + 0.6 * z;
      const zoomFinal = (larguraPalco - 2 * margem) / larguraFaixa;
      zoom = 1 + (zoomFinal - 1) * z;
      const xFim = foco - (total - 0.5) * larguraCena;
      x = xFim + (margem - xFim) * z;
      const pessoaFim = pontoFinal();
      pessoaX = (total - 0.5) * larguraCena + (pessoaFim - (total - 0.5) * larguraCena) * z;
      recolhe = t;
    }

    /* O passo 1 acontece antes de a pessoa existir: ela nasce no anel onde as portas
       do passo 2 convergem (rodada 8). */
    const pessoaO = limitar((cena - 0.85) * 4, 0, 1);
    /* No passo 5 o ponto vira o nó «contato» — e continua nó até o fim. */
    const no = limitar((cena - 3.7) * 3.3, 0, 1);

    faixa.style.setProperty('--x', `${x.toFixed(2)}px`);
    faixa.style.setProperty('--zoom', zoom.toFixed(4));
    faixa.style.setProperty('--recolhe', recolhe.toFixed(3));
    faixa.style.setProperty('--pessoa-x', `${pessoaX.toFixed(2)}px`);
    faixa.style.setProperty('--pessoa-o', pessoaO.toFixed(3));
    faixa.style.setProperty('--no', no.toFixed(3));
    fecho?.style.setProperty('--recolhe-fecho', recolhe.toFixed(3));
    setasDoFecho?.style.setProperty('--recolhe-fecho', recolhe.toFixed(3));

    /* O trilho do fecho, em px de tela: do ponto que a marcação diz (`data-de`;
       hoje a marca `utm_campaign`, 73% da cena 1) até a pessoa, que já passou da
       última marca. A faixa apaga a própria linha (`--entrega`) na mesma janela
       em que esta acende — a zoom ≈ 1 coincidem. */
    if (trilhoDoFecho) {
      const x1 = x + zoom * pontoDaFaixa(trilhoDoFecho.dataset.de, 0.73 * larguraCena);
      const x2 = x + zoom * pessoaX;
      trilhoDoFecho.style.setProperty('--fecho-x1', `${x1.toFixed(2)}px`);
      trilhoDoFecho.style.setProperty('--fecho-w', `${Math.max(0, x2 - x1).toFixed(2)}px`);
      /* Acende a zoom ≈ 1 e fica: a linha recolhida É o fecho. */
      trilhoDoFecho.style.setProperty('--fecho-o', limitar(recolhe * 4, 0, 1).toFixed(3));
    }

    cenas.forEach((el, i) => {
      el.style.setProperty('--d', limitar(i - cena, -3, 3).toFixed(3));
    });

    progresso?.style.setProperty('--p', p.toFixed(4));
    const atual = recolhe > 0.5 ? total : Math.round(cena);
    passos.forEach((li, i) => {
      if (i === atual) li.setAttribute('aria-current', 'step');
      else li.removeAttribute('aria-current');
    });
  };

  const agendar = () => {
    if (agendado || !ativo) return;
    agendado = true;
    requestAnimationFrame(pintar);
  };

  const limpar = () => {
    for (const nome of ['--x', '--zoom', '--recolhe', '--pessoa-x', '--pessoa-o', '--no']) faixa.style.removeProperty(nome);
    fecho?.style.removeProperty('--recolhe-fecho');
    setasDoFecho?.style.removeProperty('--recolhe-fecho');
    for (const nome of ['--fecho-x1', '--fecho-w', '--fecho-o']) trilhoDoFecho?.style.removeProperty(nome);
    progresso?.style.removeProperty('--p');
    for (const el of cenas) el.style.removeProperty('--d');
    for (const li of passos) li.removeAttribute('aria-current');
  };

  const ligar = () => {
    const quer = podeAnimar();
    if (quer === ativo) return;
    ativo = quer;
    secao.classList.toggle('fluxo--anima', ativo);
    if (ativo) {
      medir();
      pintar();
    } else {
      limpar();
    }
  };

  /* A barra de progresso: clicar num rótulo rola até a cena. No modo estático o
     `href="#cena-n"` já faz o navegador trazer a cena para a vista. */
  secao.addEventListener('click', (evento) => {
    const alvo = (evento.target as HTMLElement).closest<HTMLAnchorElement>('a[data-cena-alvo]');
    if (!alvo || !ativo) return;
    evento.preventDefault();
    const indice = Number(alvo.dataset.cenaAlvo);
    const pAlvo = indice >= total ? 1 : (indice / (total - 1)) * FASE_A;
    const topoDaPista = pista.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: topoDaPista - BARRA + pAlvo * percurso, behavior: 'smooth' });
  });

  window.addEventListener('scroll', agendar, { passive: true });
  window.addEventListener('resize', () => {
    ligar();
    if (ativo) {
      medir();
      agendar();
    }
  });
  menosMovimento.addEventListener('change', ligar);
  largo.addEventListener('change', ligar);
  ligar();
}

/**
 * O infográfico da dobra 6: a prova de cada módulo abre por GESTO da pessoa e
 * abre no lugar — **nenhum painel abre sozinho** (ordem da copy v4, dobra 6,
 * item 6). Um painel de cada vez: abrir um fecha o que estava aberto, «fechar»
 * volta ao anel e `Escape` também. Sem script, o anel continua legível: os doze
 * rótulos e as doze linhas estão na marcação, e só a prova fica fora de alcance.
 *
 * Duas ordens do veredito da v4 moram aqui:
 *   O2 — abrir tem de DEIXAR O PAINEL NA TELA. O painel abre abaixo do anel e
 *        empurra a dobra para baixo (isso está aprovado); o que não pode é a
 *        pessoa tocar um módulo no pé da tela e não ver nada acontecer. Por isso
 *        o painel é trazido à vista, com a margem de rolagem que desconta a barra.
 *   §6.2 — o muro da dobra 3 pode abrir o painel do módulo que fica no lugar da
 *        categoria: «proíbe-se painel que abre sem que ninguém peça; não se proíbe
 *        painel que abre porque alguém pediu de outro lugar». O `href` do muro
 *        continua `#universo`, então sem script o clique leva à dobra, como antes.
 */
function ligarInfografico(): void {
  const orbe = document.querySelector<HTMLElement>('[data-orbe]');
  if (!orbe) return;
  const modulos = [...orbe.querySelectorAll<HTMLButtonElement>('.orbe__modulo')];
  const painelDe = (botao: HTMLButtonElement) =>
    document.getElementById(botao.getAttribute('aria-controls') ?? '');

  /* Um painel tem dois gatilhos: o módulo do anel (ou da lista) e o nome no mostrador
     do telefone. Os dois carregam o estado, para o módulo aberto ficar marcado onde
     quer que a pessoa esteja a olhar. */
  const gatilhosDe = (botao: HTMLButtonElement) => [
    botao,
    ...document.querySelectorAll<HTMLElement>(`[data-modulo="${botao.id}"]`),
  ];
  const fechar = (botao: HTMLButtonElement) => {
    for (const g of gatilhosDe(botao)) g.setAttribute('aria-expanded', 'false');
    painelDe(botao)?.setAttribute('hidden', '');
  };
  const suave = window.matchMedia(MENOS_MOVIMENTO).matches ? 'auto' : 'smooth';
  const abrir = (botao: HTMLButtonElement, trazerAVista = true) => {
    for (const outro of modulos) if (outro !== botao) fechar(outro);
    for (const g of gatilhosDe(botao)) g.setAttribute('aria-expanded', 'true');
    const painel = painelDe(botao);
    painel?.removeAttribute('hidden');
    /* `start` (com a margem de rolagem que desconta a barra) põe o painel inteiro na
       tela: é a diferença entre «algo aconteceu lá embaixo» e «a prova está aqui». */
    if (trazerAVista) painel?.scrollIntoView({ behavior: suave, block: 'start' });
  };

  for (const botao of modulos) {
    botao.addEventListener('click', () => {
      if (botao.getAttribute('aria-expanded') === 'true') fechar(botao);
      else abrir(botao);
    });
  }
  orbe.addEventListener('click', (evento) => {
    const gatilho = (evento.target as HTMLElement).closest('[data-fechar]');
    if (!gatilho) return;
    const painel = gatilho.closest('.orbe__painel');
    const dono = modulos.find((m) => m.getAttribute('aria-controls') === painel?.id);
    if (!dono) return;
    fechar(dono);
    dono.focus();
  });
  document.addEventListener('keydown', (evento) => {
    if (evento.key !== 'Escape') return;
    const aberto = modulos.find((m) => m.getAttribute('aria-expanded') === 'true');
    if (!aberto) return;
    fechar(aberto);
    aberto.focus();
  });

  /* O muro da dobra 3: cada categoria com `data-modulo` abre o painel do módulo que
     fica no lugar dela. É o gesto da pessoa sendo atendido, não a página se
     desdobrando sozinha. */
  document.addEventListener('click', (evento) => {
    const categoria = (evento.target as HTMLElement).closest<HTMLElement>('[data-modulo]');
    if (!categoria) return;
    const botao = modulos.find((m) => m.id === categoria.dataset.modulo);
    if (!botao) return;
    evento.preventDefault();
    abrir(botao);
  });
}

/**
 * ── A jornada no telefone ───────────────────────────────────────────────────
 *
 * Em 390 a faixa não deita: as treze cenas empilham. Até aqui a pilha era ESTÁTICA —
 * os treze passos no estado final ao mesmo tempo —, e a dobra que é uma travessia
 * virava uma lista de treze blocos. Esta função devolve-lhe a travessia sem mudar
 * uma vírgula: escreve o MESMO `--d` do desktop, agora medido na vertical (a cena que
 * cruza a linha dos 42% da tela é o «agora»), e daí em diante são as mesmas contas e
 * as mesmas três tintas — âmbar no andado, foco no passo de agora, apagado no que vem.
 *
 * Duas diferenças de propósito em relação ao desktop: a cabeça aqui **só muda cor,
 * nunca esconde** (quem lê uma tela de cada vez precisa de tudo legível), e com
 * `prefers-reduced-motion` ela não é ligada — a pilha fica no estado final, como era.
 */
function ligarPilha(): void {
  const secao = document.querySelector<HTMLElement>('.fluxo');
  const faixa = document.querySelector<HTMLElement>('[data-faixa]');
  if (!secao || !faixa) return;
  const cenas = [...faixa.querySelectorAll<HTMLElement>('.cena[data-cena]')];
  if (cenas.length === 0) return;

  const celular = window.matchMedia(CELULAR);
  const menosMovimento = window.matchMedia(MENOS_MOVIMENTO);
  const pode = () => celular.matches && !menosMovimento.matches;
  let ativo = false;
  let agendado = false;

  const pintar = () => {
    agendado = false;
    if (!ativo) return;
    const cabeca = window.innerHeight * 0.42;
    for (const cena of cenas) {
      const caixa = cena.getBoundingClientRect();
      const meio = caixa.top + caixa.height / 2;
      const d = (meio - cabeca) / Math.max(1, caixa.height);
      cena.style.setProperty('--d', limitar(d, -3, 3).toFixed(3));
    }
  };
  const agendar = () => {
    if (agendado || !ativo) return;
    agendado = true;
    requestAnimationFrame(pintar);
  };
  const ligar = () => {
    const quer = pode();
    if (quer === ativo) return;
    ativo = quer;
    secao.classList.toggle('fluxo--pilha', ativo);
    if (ativo) pintar();
    else for (const cena of cenas) cena.style.removeProperty('--d');
  };

  window.addEventListener('scroll', agendar, { passive: true });
  window.addEventListener('resize', () => {
    ligar();
    agendar();
  });
  celular.addEventListener('change', ligar);
  menosMovimento.addEventListener('change', ligar);
  ligar();
}

function iniciar(): void {
  ligarMolduraDeEspera();
  ligarMenu();
  ajustarNoCelular();
  ligarRevelacao();
  ligarFluxo();
  ligarPilha();
  ligarInfografico();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', iniciar, { once: true });
} else {
  iniciar();
}

/* Sem isto o TypeScript trata o arquivo como script global e as declarações
   vazam para todo `src/`. Um `export {}` o torna módulo sem importar nada. */
export {};
